import { Box, useTheme } from '@mui/material'
import {
  DatePicker as AntDatePicker,
  DatePickerProps as AntDatePickerProps,
} from 'antd'
import { FC, ReactNode } from 'react'
import { themeMeta } from '../theme/meta'

const DatePicker: FC<DatePickerProps> = ({ trigger, ...passingProps }) => {
  const theme = useTheme()

  if (trigger) {
    return (
      <Box
        position="relative"
        sx={{
          '& .ant-picker': {
            position: 'absolute',
            insetInlineStart: 0,
            insetBlockStart: 0,
            inlineSize: '100%',
            blockSize: '100%',
            opacity: 0,

            '&, & *': {
              cursor: 'pointer',
            },
          },
        }}
      >
        {trigger}
        <AntDatePicker
          allowClear={false}
          panelRender={(panel) => (
            <Box
              sx={{
                '.ant-picker-dropdown &': {
                  fontFamily: theme.typography.fontFamily,

                  '& button': {
                    fontFamily: 'inherit',
                  },
                  '& .ant-picker-panel-container': {
                    border: `2px solid ${theme.palette.divider}`,
                    borderRadius: theme.spacing(1),
                    boxShadow: 'none',

                    '& .ant-picker-panel': {
                      borderWidth: 0,
                    },
                  },
                  '& .ant-picker-panel .ant-picker-footer': {
                    borderTopWidth: 2,
                    borderColor: theme.palette.divider,
                  },
                  '& .ant-picker-header': {
                    paddingInline: theme.spacing(3),
                    paddingBlockStart: theme.spacing(2),
                    borderBottom: 'none',
                  },
                  '& .ant-picker-header-view button': {
                    fontWeight: 'bold',
                    fontSize: 20,

                    '&:hover': {
                      color: theme.palette.primary.main,
                    },
                  },
                  '& .ant-picker-prev-icon, & .ant-picker-next-icon, & .ant-picker-super-prev-icon, & .ant-picker-super-next-icon':
                    {
                      width: 10,
                      height: 10,
                    },
                  '& .ant-picker-body': {
                    paddingInline: theme.spacing(3),

                    '& table': {
                      margin: 0,
                    },
                  },
                  '& .ant-picker-content th, & .ant-picker-content td': {
                    fontWeight: 600,
                  },
                  '& .ant-picker-content th': {
                    textAlign: 'center',
                    color: themeMeta.colors.monochrome.placehold,
                  },
                  '& .ant-picker-cell .ant-picker-cell-inner': {
                    borderRadius: 99,
                    textDecoration: 'underline',
                  },
                  '& .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before':
                    {
                      borderColor: theme.palette.text.primary,
                      borderRadius: 'inherit',
                    },
                  '& .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner':
                    {
                      background: theme.palette.text.primary,
                    },
                  '& .ant-picker-footer': {
                    borderBottom: 'none',
                    lineHeight: 'revert',
                    padding: theme.spacing(2),
                    textAlign: 'right',
                  },
                  '& .ant-picker-ranges': {
                    justifyContent: 'end',
                  },
                  '& .ant-picker-now': {
                    lineHeight: 'initial',
                  },
                  '& .ant-picker-now-btn': {
                    display: 'inline-block',
                    padding: '0.5em 1em',
                    fontWeight: 600,
                    color: themeMeta.colors.monochrome.offWhite,
                    background: theme.palette.text.primary,
                    borderRadius: 99,

                    '&:hover': {
                      background: themeMeta.colors.monochrome.body,
                    },
                  },
                },
              }}
            >
              {panel}
            </Box>
          )}
          {...passingProps}
        />
      </Box>
    )
  }

  return <AntDatePicker {...passingProps} />
}

type DatePickerProps = Pick<
  AntDatePickerProps,
  'minDate' | 'maxDate' | 'value' | 'onChange'
> & {
  trigger?: ReactNode
}

export { DatePicker }
export type { DatePickerProps }
